import type { TCommon, TGeneral, TRecord } from '~/src/types/common'
import { LITHIUM, NICKEL } from '~/constants/services/constants'
import {
  getAllowedOptionsServicePro,
  getAllowedProductsServicePro,
  getBrotherOptions,
  getLevelOptions,
} from '~/utils/chart'
import isEmpty from 'lodash/isEmpty'

type TParseFilters = {
  category: string
  chartOptions: TRecord
}

type TOption = {
  id: string
  name: string
  checked: boolean
  disabled: boolean
}

type TGetNewFilter = {
  filter: TGeneral
  combinations: TGeneral[]
  selectedOptions: Record<string, TOption[]>
}

const getDataWithHashes = (axis: TRecord, hashes: string[]) => {
  if (!axis) return undefined
  if (hashes.length <= 0) return axis

  const data = axis.data.map((item: TCommon) => ({
    ...item,
    hidden: !hashes.includes(item.hash),
  }))

  return { ...axis, data }
}

export const getDataByHashes = (rawData: TRecord, hashes?: string[]) => {
  const hasHashes = hashes.length > 0

  if (!hasHashes) return rawData

  const y1 = getDataWithHashes(rawData.y1, hashes)
  const y2 = getDataWithHashes(rawData.y2, hashes)

  return { y1, y2 }
}

const markets_: string[] = [LITHIUM.slug, NICKEL.slug]

const getValueByKey = (data: TCommon[], category, key) => {
  if (markets_.includes(category) && key === 'categories') {
    return data.filter(Boolean).map((value: TCommon) => ({
      name: value?.data.name,
      isSustainable: value?.data.isSustainable,
    }))
  }
  return data.filter(Boolean).map((value: TCommon) => value?.data.name)
}

export const parseFilters = ({ chartOptions, category }: TParseFilters) => {
  return Object.keys(chartOptions).reduce((acc: TRecord, key) => {
    const valueKeys = getValueByKey(chartOptions[key], category, key)

    if (valueKeys.length > 0) {
      return { ...acc, [key]: valueKeys }
    }

    return acc
  }, {})
}

export const getNewFilter = ({
  filter,
  combinations,
  selectedOptions,
}: TGetNewFilter) => {
  const levelOptions = getLevelOptions(selectedOptions, filter.level)
  const allowedProducts = getAllowedProductsServicePro(
    combinations,
    levelOptions,
  )
  const allowedOptions = getAllowedOptionsServicePro(allowedProducts)
  const brotherIds = getBrotherOptions(selectedOptions)

  const options = filter.options.map((option) => {
    let isDisabled =
      !allowedOptions.includes(option.data.name) ||
      brotherIds.includes(option.data.id)

    if (option.parent) {
      const parentSelectedOptions = selectedOptions[option.parent]

      if (isEmpty(parentSelectedOptions)) {
        isDisabled = true
      }
    }

    const isChecked = isDisabled ? false : option.checked

    return { ...option, checked: isChecked, disabled: isDisabled }
  })

  return { ...filter, options }
}

export const getSelectedOptions = (filters) => {
  const selected: Record<string, TOption[]> = {}

  filters.forEach((_item) => {
    selected[_item.name] = _item.options.filter((option) => option.checked)
  })

  return selected
}
